// @erp/module-cms/schemas — the COOKIE / CONSENT registry wire contracts. Browser-safe:
// zod + plain data only (the admin form, the gateway, AND the external site's consent
// banner all parse the SAME schemas, so none of them can disagree on what a valid cookie
// entry or a valid category is). Mirrors the categories registry shape (a create/update/
// DTO/list-query family) — but a cookie is SITE CONFIG, not a content type: no slug, no
// status-lifecycle, no soft-delete.
//
// The four CATEGORIES are the standard consent-banner buckets and their labels/descriptions
// are CODE-OWNED (CATEGORY_META below), not stored per-row: the banner always renders the
// full set of four (even an empty category), so the copy is a fixed contract, not data.
import { z } from "zod";
import { EntityId, EpochMs } from "@erp/core-schemas";

// ── The consent categories (a fixed set ⇒ an enum, so a bad category 422s) ──────
export const COOKIE_CATEGORIES = ["necessary", "functional", "analytics", "marketing"] as const;
export const CookieCategory = z.enum(COOKIE_CATEGORIES);
export type CookieCategory = z.infer<typeof CookieCategory>;

/**
 * The fixed per-category banner copy — a label + a one-line description, the standard
 * consent-banner text. CODE-OWNED (never stored), so every deployment's banner reads the
 * same wording and every category is present even when it holds no cookies. The public
 * read (groupByCategory) attaches this to each of the four buckets.
 */
export const CATEGORY_META: Record<CookieCategory, { label: string; description: string }> = {
  necessary: { label: "Strictly necessary", description: "Required for the site to function" },
  functional: { label: "Functional", description: "Remember preferences" },
  analytics: { label: "Analytics", description: "Understand usage" },
  marketing: { label: "Marketing", description: "Personalized content/ads" },
};

// ── The write-side field validators (one source, Create/Update both derive) ────
const cookieCore = {
  category: CookieCategory,
  name: z.string().trim().min(1).max(200),
  /** Nullable so a PATCH can clear it back to unset. */
  provider: z.string().trim().max(200).nullable(),
  purpose: z.string().trim().max(2000).nullable(),
  duration: z.string().trim().max(100).nullable(),
  sortOrder: z.number().int().min(0),
};

/**
 * Create body — category + name required; provider/purpose/duration OPTIONAL (nullable
 * free text); sortOrder carries the DDL default (0).
 */
export const CreateCmsCookie = z.object({
  category: cookieCore.category,
  name: cookieCore.name,
  provider: cookieCore.provider.optional(),
  purpose: cookieCore.purpose.optional(),
  duration: cookieCore.duration.optional(),
  sortOrder: cookieCore.sortOrder.default(0),
});
export type CreateCmsCookie = z.infer<typeof CreateCmsCookie>;

/** Update body — a partial PATCH over the same fields. */
export const UpdateCmsCookie = z.object(cookieCore).partial();
export type UpdateCmsCookie = z.infer<typeof UpdateCmsCookie>;

/** List query — optional `?category` filter; omitted lists every category. */
export const CmsCookieListQuery = z.object({ category: CookieCategory.optional() });
export type CmsCookieListQuery = z.infer<typeof CmsCookieListQuery>;

/** The admin wire DTO — the read shape (camelCase; audit actor columns stay server-side). */
export const CmsCookie = z.object({
  id: EntityId,
  category: CookieCategory,
  name: z.string(),
  provider: z.string().nullable(),
  purpose: z.string().nullable(),
  duration: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
});
export type CmsCookie = z.infer<typeof CmsCookie>;

/** One cookie as it appears INSIDE a public consent group (no ids, no audit — just the banner facts). */
export const CmsPublicCookie = z.object({
  name: z.string(),
  provider: z.string().nullable(),
  purpose: z.string().nullable(),
  duration: z.string().nullable(),
});
export type CmsPublicCookie = z.infer<typeof CmsPublicCookie>;

/** One consent category with its fixed meta + the cookies nested under it (the banner's per-bucket shape). */
export const CmsConsentGroup = z.object({
  id: CookieCategory,
  label: z.string(),
  description: z.string(),
  cookies: z.array(CmsPublicCookie),
});
export type CmsConsentGroup = z.infer<typeof CmsConsentGroup>;

/** The PUBLIC consent payload — ALWAYS all four categories (even empty ones), in the COOKIE_CATEGORIES order. */
export const CmsConsentPayload = z.object({ categories: z.array(CmsConsentGroup) });
export type CmsConsentPayload = z.infer<typeof CmsConsentPayload>;
